import { MultibuyPriceLevel } from '~/api/Catalog';

/**
 * Returns the highest applicable multibuy level for the given quantity.
 * Levels must be sorted by quantity in ascending order.
 * Returns the level with the maximum quantity, for which quantity >= level.quantity
 */
export function getApplicableMultibuyLevel(
  levels: MultibuyPriceLevel[] | null | undefined,
  quantity: number,
): MultibuyPriceLevel | null {
  if (!levels || levels.length === 0 || quantity <= 0) {
    return null;
  }

  // Find the highest applicable level (with the maximum quantity)
  let applicableLevel: MultibuyPriceLevel | null = null;

  for (const level of levels) {
    if (quantity >= level.quantity) {
      if (!applicableLevel || level.quantity > applicableLevel.quantity) {
        applicableLevel = level;
      }
    }
  }

  return applicableLevel;
}

/**
 * Returns the lowest multibuy level (with the minimum quantity).
 * Used to display information about the minimum quantity for multibuy activation.
 */
export function getLowestMultibuyLevel(
  levels: MultibuyPriceLevel[] | null | undefined,
): MultibuyPriceLevel | null {
  if (!levels || levels.length === 0) {
    return null;
  }

  // Find the level with the minimum quantity
  return levels.reduce(
    (lowest, current) => {
      return !lowest || current.quantity < lowest.quantity ? current : lowest;
    },
    null as MultibuyPriceLevel | null,
  );
}

/**
 * Checks if multibuy is active for the given quantity.
 * Multibuy is active if there is an applicable level for the given quantity.
 */
export function isMultibuyActive(
  levels: MultibuyPriceLevel[] | null | undefined,
  quantity: number,
): boolean {
  return getApplicableMultibuyLevel(levels, quantity) !== null;
}

/**
 * Sorts multibuy levels by quantity in ascending order.
 * This is useful if levels come unsorted from the backend.
 */
export function sortMultibuyLevels(
  levels: MultibuyPriceLevel[] | null | undefined,
): MultibuyPriceLevel[] {
  if (!levels || levels.length === 0) {
    return [];
  }

  return [...levels].sort((a, b) => a.quantity - b.quantity);
}
